import {
  Box,
  Button,
  Heading,
  HiddenVisually,
  IconInfo,
  IconPromote,
  IconTick,
  Inline,
  Stack,
  Text,
  TooltipRenderer,
} from 'braid-design-system';
import type { ReactNode } from 'react';

import * as styles from './DesktopPackageColumns.css';

interface DesktopPackageColumnsProps {
  onFreemiumClick: () => void;
  onStandardClick: () => void;
  onAccelerateClick: () => void;
  htags: {
    freemium: string;
    standard: string;
    accelerate: string;
  };
}

export const DesktopPackageColumns = (props: DesktopPackageColumnsProps) => (
  <Box>
    <Box className={styles.row} background="surface">
      <Box className={styles.cellFirstRow}>
        <EmptyCell />
      </Box>

      <Box className={styles.cellFirstRow}>
        <EmptyCell />
      </Box>

      <Box className={styles.cellFirstRow}>
        <EmptyCell />
      </Box>

      <Box className={styles.cellFirstRow}>
        <Text
          icon={<IconPromote />}
          align="center"
          weight="strong"
          tone="promote"
        >
          Best performing
        </Text>
      </Box>
    </Box>

    <Box className={styles.row} background="surface">
      <Box className={styles.cellHeading}>
        <EmptyCell />
      </Box>

      <Box
        className={styles.cellHighlightBrandAccent}
        background="surface"
        data={{ testid: 'desktop-package-header-cell-freemium' }}
      >
        <Stack space="medium">
          <Text tone="brandAccent" size="large" weight="strong">
            Free
          </Text>

          <Text>Starting your selling journey</Text>
        </Stack>
      </Box>

      <Box
        className={styles.cellHighlightBrandAccent}
        background="surface"
        data={{ testid: 'desktop-package-header-cell-standard' }}
      >
        <Stack space="medium">
          <Text tone="brandAccent" size="large" weight="strong">
            Standard
          </Text>

          <Text>Get noticed by more buyers</Text>

          <Stack space="small" data={{ testid: 'package-price' }}>
            <Heading weight="weak" level="2" align="center">
              $949
            </Heading>
            <Text size="small">+ GST</Text>
          </Stack>
        </Stack>
      </Box>

      <Box
        className={styles.cellHighlightPromote}
        background="surface"
        data={{ testid: 'desktop-package-header-cell-accelerate' }}
      >
        <Stack space="medium">
          <Text tone="promote" size="large" weight="strong">
            Accelerate
          </Text>

          <Text>Reach the right buyers faster</Text>

          <Stack space="small" data={{ testid: 'package-price' }}>
            <Heading weight="weak" level="2" align="center">
              $1299
            </Heading>
            <Text size="small">+ GST</Text>
          </Stack>
        </Stack>
      </Box>
    </Box>

    <Box className={styles.row} background="neutralLight">
      <Box className={styles.cellHeading}>
        <RowHeading
          title="Listing duration"
          description="How long your business stays on-site"
        />
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <TextCell>6 months</TextCell>
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <TextCell>Until sold</TextCell>
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <TextCell>Until sold</TextCell>
      </Box>
    </Box>

    <Box className={styles.row} background="surface">
      <Box className={styles.cellHeading}>
        <RowHeading
          title="Search appearances"
          description="How often your business appears in potential buyers' search results"
          tooltipMessage={PERFORMANCE_TOOLTIP_MESSAGE}
        />
      </Box>

      <Box className={styles.cell} background="surface">
        <TextCell>1x</TextCell>
      </Box>

      <Box className={styles.cell} background="surface">
        <TextCell>4.4x</TextCell>
      </Box>

      <Box className={styles.cell} background="surface">
        <TextCell>6.8x</TextCell>
      </Box>
    </Box>

    <Box className={styles.row} background="neutralLight">
      <Box className={styles.cellHeading}>
        <RowHeading
          title="Views by potential buyers"
          description="How often potential buyers view your business"
          tooltipMessage={PERFORMANCE_TOOLTIP_MESSAGE}
        />
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <TextCell>1x</TextCell>
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <TextCell>2.2x</TextCell>
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <TextCell>4.5x</TextCell>
      </Box>
    </Box>

    <Box className={styles.row} background="surface">
      <Box className={styles.cellHeading}>
        <RowHeading
          title="Photo shown in search results"
          description="Buyers see a preview photo from your listing when searching on-site"
        />
      </Box>

      <Box className={styles.cell} background="surface">
        <UpgradeCell />
      </Box>

      <Box className={styles.cell} background="surface">
        <TickCell />
      </Box>

      <Box className={styles.cell} background="surface">
        <TickCell />
      </Box>
    </Box>

    <Box className={styles.row} background="neutralLight">
      <Box className={styles.cellHeading}>
        <RowHeading
          title="Reach buyers off-site"
          description="Your business is recommended to buyers via email"
        />
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <UpgradeCell />
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <TickCell />
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <TickCell />
      </Box>
    </Box>

    <Box className={styles.row} background="surface">
      <Box className={styles.cellHeading}>
        <RowHeading
          title="Promoted placement on-site"
          description="Your business is featured at the top of search results"
        />
      </Box>

      <Box className={styles.cell} background="surface">
        <UpgradeCell />
      </Box>

      <Box className={styles.cell} background="surface">
        <UpgradeCell />
      </Box>

      <Box className={styles.cell} background="surface">
        <TickCell />
      </Box>
    </Box>

    <Box className={styles.row} background="neutralLight">
      <Box className={styles.cellHeading}>
        <RowHeading
          title="Promoted placement off-site"
          description="Your business is featured at the top of buyer recommendation emails"
        />
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <UpgradeCell />
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <UpgradeCell />
      </Box>

      <Box className={styles.cell} background="neutralLight">
        <TickCell />
      </Box>
    </Box>

    <Box className={styles.row} background="surface">
      <Box className={styles.cellHeading}>
        <EmptyCell />
      </Box>

      <Box display="flex" className={styles.footerCell} background="surface">
        <Button
          tone="formAccent"
          onClick={props.onFreemiumClick}
          data={{ htag: props.htags.freemium }}
        >
          Choose Free
        </Button>
      </Box>

      <Box display="flex" className={styles.footerCell} background="surface">
        <Button
          tone="formAccent"
          onClick={props.onStandardClick}
          data={{ htag: props.htags.standard }}
        >
          Choose Standard
        </Button>
      </Box>

      <Box display="flex" className={styles.footerCell} background="surface">
        <Button
          tone="formAccent"
          onClick={props.onAccelerateClick}
          data={{ htag: props.htags.accelerate }}
        >
          Choose Accelerate
        </Button>
      </Box>
    </Box>
  </Box>
);

const PERFORMANCE_TOOLTIP_MESSAGE =
  'Numbers are based on the average performance of paid listings compared to Free listings between July 2025 – June 2026.';

interface RowHeadingProps {
  title: string;
  description: string;
  tooltipMessage?: string;
}

const RowHeading = (props: RowHeadingProps) => (
  <Stack space="xsmall">
    <Inline space="xsmall" alignY="center">
      <Text weight="strong">{props.title}</Text>
      {props.tooltipMessage ? (
        <TooltipRenderer
          tooltip={<Text size="small">{props.tooltipMessage}</Text>}
        >
          {({ triggerProps }) => (
            <Box {...triggerProps} display="flex" alignItems="center">
              <IconInfo size="xsmall" tone="formAccent" />
            </Box>
          )}
        </TooltipRenderer>
      ) : null}
    </Inline>
    <Text size="small">{props.description}</Text>
  </Stack>
);

interface TextCellProps {
  children: ReactNode;
}

const TextCell = (props: TextCellProps) => (
  <Box display="flex" height="full" alignItems="center" justifyContent="center">
    <Text size="small">{props.children}</Text>
  </Box>
);

const TickCell = () => (
  <Box display="flex" height="full" alignItems="center" justifyContent="center">
    <IconTick tone="info" size="large" />
  </Box>
);

const UpgradeCell = () => (
  <Box display="flex" height="full" alignItems="center" justifyContent="center">
    <Text size="small">Upgrade</Text>
  </Box>
);

const EmptyCell = () => (
  <Box>
    <HiddenVisually>Not offered</HiddenVisually>
  </Box>
);
